package com.essntl.features.paymentparameter.ui.addeditpaymentparameter.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.focusProperties
import com.essntl.features.paymentparameter.domain.model.PaymentParameterModel
import com.essntl.features.paymentparameter.ui.addeditpaymentparameter.AddEditPaymentParameterEvent
import com.essntl.features.paymentparameter.ui.addeditpaymentparameter.AddEditPaymentParameterState
import com.essntl.features.paymentparameter.ui.addeditpaymentparameter.AddEditPaymentParameterValidation
import com.essntl.uikit.components.checkbox.EsPrimaryCheckBox
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.label.EsInputLabel
import com.essntl.uikit.components.input.money.EsMoneyInput
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun PaymentParameterInformation(
    state: AddEditPaymentParameterState,
    validation: AddEditPaymentParameterValidation,
    onEvent: (AddEditPaymentParameterEvent) -> Unit,
    enabled: Boolean,
) {
    val paymentParameter = state.paymentParameter.value
    val isLoading = state.addEditPaymentParameter.isLoading

    Column(
        modifier =
        Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
            ),
    ) {
        Spacer(modifier = Modifier.height(1.spacing))

        // Method
        EsInputLabel(label = "Method")
        Spacer(modifier = Modifier.height(.5.spacing))
        PaymentParameterModel.Method.entries.map { method ->
            Spacer(
                modifier = Modifier.height(1.spacing),
            )

            Row(
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                EsPrimaryCheckBox(
                    checked = paymentParameter.method == method,
                    enabled = enabled && !isLoading,
                    onCheckedChange = {
                        onEvent(
                            AddEditPaymentParameterEvent.UpdatePaymentParameterFields(
                                paymentParameter.copy(method = method),
                            ),
                        )
                    },
                    modifier = Modifier
                        .focusProperties { canFocus = false }
                        .align(Alignment.CenterVertically),
                )

                Spacer(modifier = Modifier.width(2.spacing))

                Text(
                    text = "${
                        if (method == PaymentParameterModel.Method.Add)
                            "Adding to" else "Subtracting from"
                    } amount",
                    style = MaterialTheme.esTypography.p1Regular16,
                    color = MaterialTheme.esColorScheme.textPrimary,
                )
            }
        }

        Spacer(modifier = Modifier.height(2.spacing))

        // Type
        EsInputLabel(label = "Type")
        Spacer(modifier = Modifier.height(.5.spacing))
        PaymentParameterModel.Type.entries.map { type ->
            Spacer(
                modifier = Modifier.height(1.spacing),
            )

            Row(
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                EsPrimaryCheckBox(
                    checked = paymentParameter.type == type,
                    enabled = enabled && !isLoading,
                    onCheckedChange = {
                        onEvent(
                            AddEditPaymentParameterEvent.UpdatePaymentParameterFields(
                                paymentParameter.copy(
                                    type = type,
                                    amount = null,
                                ),
                            ),
                        )
                    },
                    modifier = Modifier
                        .focusProperties { canFocus = false }
                        .align(Alignment.CenterVertically),
                )

                Spacer(modifier = Modifier.width(2.spacing))

                Text(
                    text = if (type == PaymentParameterModel.Type.FlatFee)
                        "Flat fee" else "Percentage",
                    style = MaterialTheme.esTypography.p1Regular16,
                    color = MaterialTheme.esColorScheme.textPrimary,
                )
            }
        }

        Spacer(modifier = Modifier.height(2.spacing))

        EsInput(
            value = paymentParameter.name ?: "",
            onValueChange = {
                validation.addEditPaymentParameterForm.name.validate(it)
                onEvent(
                    AddEditPaymentParameterEvent.UpdatePaymentParameterFields(
                        paymentParameter.copy(name = it),
                    ),
                )
            },
            state = validation.addEditPaymentParameterForm.name.state,
            message = "This field is required",
            placeholder = "Enter Name",
            label = "Name",
            enabled = enabled && !isLoading,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(2.spacing))

        EsMoneyInput(
            value = (paymentParameter.amount ?: 0) / 100.0,
            onValueChange = {
                validation.addEditPaymentParameterForm.amount.validate(
                    if (it.toInt() == 0) "" else it.toInt().toString(),
                )

                val amount = (it * 100).toInt()

                onEvent(
                    AddEditPaymentParameterEvent.UpdatePaymentParameterFields(
                        paymentParameter.copy(amount = amount),
                    ),
                )
            },
            label = "Amount",
            enabled = enabled && !isLoading,
            state = validation.addEditPaymentParameterForm.amount.state,
            message = "This field is required",
            currency = if (paymentParameter.type == PaymentParameterModel.Type.FlatFee) "$" else "%",
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(2.spacing))

        Row(
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            EsPrimaryCheckBox(
                checked = paymentParameter.isInvoice ?: false,
                enabled = enabled && !isLoading,
                onCheckedChange = {
                    onEvent(
                        AddEditPaymentParameterEvent.UpdatePaymentParameterFields(
                            paymentParameter.copy(isInvoice = it),
                        ),
                    )
                },
                modifier = Modifier
                    .focusProperties { canFocus = false }
                    .align(Alignment.CenterVertically),
            )

            Spacer(modifier = Modifier.width(2.spacing))

            Text(
                text = "For invoice (if left unchecked, this parameter will be for Payment links)",
                style = MaterialTheme.esTypography.p1Regular16,
                color = MaterialTheme.esColorScheme.textPrimary,
            )
        }

        Spacer(modifier = Modifier.height(2.spacing))

        Row(
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            EsPrimaryCheckBox(
                checked = paymentParameter.applyToAmount ?: false,
                enabled = enabled && !isLoading,
                onCheckedChange = {
                    onEvent(
                        AddEditPaymentParameterEvent.UpdatePaymentParameterFields(
                            paymentParameter.copy(applyToAmount = it),
                        ),
                    )
                },
                modifier = Modifier
                    .focusProperties { canFocus = false }
                    .align(Alignment.CenterVertically),
            )

            Spacer(modifier = Modifier.width(2.spacing))

            Text(
                text = "Apply To Amount",
                style = MaterialTheme.esTypography.p1Regular16,
                color = MaterialTheme.esColorScheme.textPrimary,
            )
        }
    }
}